import { useSelector } from "react-redux";
import { useContext, useState } from "react";
import { isFlagEnabled } from "js/utilities";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { Link, useLocation } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { ChatContext } from "context/Agent/ChatProvider";

import MailCenterCard from "./MailCenterCard";
import useCustomerTopThreeFAQs from "./useCustomerTopThreeFAQs";
import CallCustomerServiceCard from "./CallCustomerServiceCard";

import Carousel from "components/General/Carousel";
import Accordion from "components/General/Accordion";
import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import ExploreOurProducts from "components/General/ExploreOurProducts";
import CallToActionTextOverImage from "components/General/CallToActionTextOverImage";
import CallToActionCardWithIconOnTop from "components/General/CallToActionCardWithIconOnTop";

export default function Help() {
  const { t } = useTranslation();
  const { user } = useSelector((s) => s.user);
  const { policies } = useSelector((s) => s.data);
  const location = useLocation();
  const topThreeFAQs = useCustomerTopThreeFAQs();
  const [isOpenEmailUs, setIsOpenEmailUs] = useState(false);
  const { setOpenChatWindow, openChatWindow } = useContext(ChatContext);

  useBreadcrumb(
    location.pathname.indexOf("guest") > 0
      ? [
          {
            name: t("CustomerHelp-home"),
            url: "/",
          },
          { name: t("CustomerHelp-help"), url: "#" },
        ]
      : [
          {
            name: t("CustomerHelp-home"),
            url: "/policies",
          },
          { name: t("CustomerHelp-help"), url: "#" },
        ]
  );
  return (
    <>
      <div className="container mb-8">
        <h1>{t("CustomerHelp-title")}</h1>
        <p>{t("CustomerHelp-subTitle")}</p>
        <hr />
      </div>

      <div className="container ui-component-spacing">
        <CallToActionTextOverImage
          backgroundImagePath="/assets/img/cta-help-placeholder.jpg"
          backgroundImageAltText={t("CustomerHelp-bannerTitle")}
          backgroundImageOpacity=".2"
          headline={t("CustomerHelp-bannerTitle")}
          tagline={t("CustomerHelp-bannerSubTitle")}
        />
      </div>

      {/* FAQs */}
      {isFlagEnabled("ENABLE_EVL_PRODUCT_INFO_ON_HELP_PAGE") ? (
        <div className="container ui-component-spacing grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <h2>{t("CustomerHelp-FAQsTitle")}</h2>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
              <div className="flex-grow mr-8">
                <p className="text-sm">{t("CustomerHelp-FAQsSubTitle")}</p>
              </div>
              <div className="flex-shrink-0 mt-4 sm:mt-0">
                <Trans
                  i18nKey="CustomerHelp-FAQsViewAll"
                  components={{
                    link1: (
                      <Link
                        to={
                          user?.userId
                            ? "/help/help-faqs"
                            : "/guest/help/help-faqs"
                        }
                        className="ui-btn-primary"
                      ></Link>
                    ),
                  }}
                ></Trans>
              </div>
            </div>
            <hr />
            <div className="grid grid-cols-1 gap-4">
              {topThreeFAQs.map((faq) => (
                <Accordion item={faq} key={`FAQ - ${JSON.stringify(faq)}`} />
              ))}
            </div>
          </div>
          <div>
            <ExploreOurProducts
              productLinks={[
                {
                  name: t("ExploreOurProducts-product-1-name"),
                  url: t("ExploreOurProducts-product-1-url"),
                },
              ]}
            />
          </div>
        </div>
      ) : (
        <div className="container ui-component-spacing">
          <h2>{t("CustomerHelp-FAQsTitle")}</h2>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
            <div className="flex-grow mr-8">
              <p className="text-sm">{t("CustomerHelp-FAQsSubTitle")}</p>
            </div>
            <div className="flex-shrink-0 mt-4 sm:mt-0">
              <Trans
                i18nKey="CustomerHelp-FAQsViewAll"
                components={{
                  link1: (
                    <Link
                      to={
                        user?.userId
                          ? "/help/help-faqs"
                          : "/guest/help/help-faqs"
                      }
                      className="ui-btn-primary"
                    ></Link>
                  ),
                }}
              ></Trans>
            </div>
          </div>
          <hr />
          <div className="grid grid-cols-1 gap-4">
            {topThreeFAQs.map((faq) => (
              <Accordion item={faq} key={`FAQ - ${JSON.stringify(faq)}`} />
            ))}
          </div>
        </div>
      )}

      {/* Contact Us section */}
      <div id="contact-us" className="container ui-component-spacing">
        <h2>{t("CustomerHelp-contactTitle-1")}</h2>
        <p className="text-sm">{t("CustomerHelp-contactSubTitle")}</p>
        <hr />
        <div className="grid gap-4 grid-cols-1 auto-rows-min content-start md:gap-6 md:grid-cols-3 md:grid-row-auto">
          <div className="flex gap-2 md:grid md:gap-y-4">
            <div className="w-full">
              <CallCustomerServiceCard />
            </div>
            {/* live chat */}
            {isFlagEnabled("ENABLE_CUSTOMER_PORTAL_CHAT_FUNCTION") && (
              <div className="w-full">
                <CallToActionCardWithIconOnTop
                  icon="#ui-icon-chat-alt"
                  action="modal"
                  title={
                    <Trans
                      i18nKey={"CustomerHelp-contactAction-5-title"}
                      components={{
                        span1: (
                          <span className="font-body font-bold text-xl text-black" />
                        ),
                      }}
                    />
                  }
                  desc={
                    <>
                      <Trans
                        i18nKey={"CallCustomerServiceCard-hoo-chat"}
                        components={{
                          span1: (
                            <span className="text-sm font-bold text-black" />
                          ),
                        }}
                      />
                      <Trans
                        i18nKey={"CustomerHelp-contactAction-5-description"}
                        components={{
                          break1: <br />,
                          break2: <br />,
                          span1: <span className="text-black" />,
                        }}
                      />
                    </>
                  }
                  ctaText={t("CustomerHelp-contactAction-6-ctaText")}
                  ctaSrText={t("CustomerHelp-contactAction-5-ctaSrText")}
                  actionMethod={() => setOpenChatWindow(!openChatWindow)}
                  isActionAtTheBottom={true}
                />
              </div>
            )}

            {/* email us */}
            <div className="w-full">
              <CallToActionCardWithIconOnTop
                action="modal"
                actionMethod={() => setIsOpenEmailUs(true)}
                icon="#ui-icon-message"
                title={
                  <Trans
                    i18nKey={"CustomerHelp-contactAction-4-title"}
                    components={{
                      span1: (
                        <span className="font-body font-bold text-xl text-black" />
                      ),
                    }}
                  />
                }
                desc={
                  <Trans
                    i18nKey={"CustomerHelp-contactAction-4-email-description"}
                    components={{
                      span1: <span className="text-black" />,
                    }}
                  />
                }
                ctaText={t("CustomerHelp-contactAction-4-ctaText")}
                ctaSrText={t("CustomerHelp-contactAction-4-ctaSrText")}
                ctaUrl="#"
                isActionAtTheBottom={true}
              />
            </div>
          </div>
          <div className="grid md:col-span-2">
            <MailCenterCard />
          </div>
        </div>
      </div>

      <ContactUsDialog
        dialogState={isOpenEmailUs}
        setDialogState={setIsOpenEmailUs}
      />
    </>
  );
}
